import { initializeApp } from "firebase/app";
import { getAnalytics, isSupported as isAnalyticsSupported, Analytics } from "firebase/analytics";
import {
  isSupported as isMessagingSupported,
  getMessaging,
  register,
  onRegistered,
  onMessage,
  MessagePayload,
  Messaging,
} from "firebase/messaging";

const VAPID_KEY = "BASZuQtQWId-H4XyHxofpjH7rbdyYARO8_mrAs71meO8QDhyUJcSEBz6VO50ZpbQPv37CBA883ikigtP84phEAY";

const firebaseConfig = {
  apiKey: "AIzaSyB0MGj0BOlgICAB94I28l85hgaOsbgJciw",
  authDomain: "sanoft-pos-legacy.firebaseapp.com",
  projectId: "sanoft-pos-legacy",
  storageBucket: "sanoft-pos-legacy.appspot.com",
  messagingSenderId: "692253515702",
  appId: "1:692253515702:web:b3b7e168be7b137c272b17",
  measurementId: "G-RXJ0ZDSNY8",
};

const firebaseApp = initializeApp(firebaseConfig);

let analyticsPromise: Promise<Analytics | null> | null = null;
async function getAnalyticsInstance(): Promise<Analytics | null> {
  if (!analyticsPromise) {
    analyticsPromise = (async () => {
      try {
        const supported = await isAnalyticsSupported();
        if (supported) {
          return getAnalytics(firebaseApp);
        }
      } catch (err) {
        console.warn("Firebase Analytics is not supported in this environment:", err);
      }
      return null;
    })();
  }
  return analyticsPromise;
}

let messagingPromise: Promise<Messaging | null> | null = null;
async function getMessagingInstance(): Promise<Messaging | null> {
  if (!messagingPromise) {
    messagingPromise = (async () => {
      try {
        const supported = await isMessagingSupported();
        if (supported) {
          return getMessaging(firebaseApp);
        }
      } catch (err) {
        console.warn("Firebase Messaging is not supported in this environment:", err);
      }
      return null;
    })();
  }
  return messagingPromise;
}

async function reCreateFcmToken(): Promise<string | null> {
  try {
    const firebaseMessaging = await getMessagingInstance();
    if (!firebaseMessaging) {
      console.warn("FCM messaging is not supported in this browser.");
      return null;
    }

    if (typeof window === "undefined" || !("Notification" in window)) {
      console.warn("Notification API not supported in this browser.");
      return null;
    }

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      throw new Error("Permission denied. Please allow notifications in your browser settings.");
    }

    if ("serviceWorker" in navigator) {
      await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    }

    return new Promise<string>((resolve, reject) => {
      onRegistered(firebaseMessaging, (installationId: string) => {
        if (!installationId) {
          reject(new Error("FCM installation ID not obtained"));
          return;
        }
        resolve(installationId);
      });
      register(firebaseMessaging, { vapidKey: VAPID_KEY });
    });
  } catch (error) {
    console.warn("Failed to recreate FCM token:", error);
    return null;
  }
}

function onForegroundMessage(callback: (payload: MessagePayload) => void) {
  let unsubscribe: (() => void) | null = null;

  getMessagingInstance()
    .then((firebaseMessaging) => {
      if (firebaseMessaging) {
        unsubscribe = onMessage(firebaseMessaging, (payload) => {
          console.log("Foreground FCM message received: ", payload);
          callback(payload);
        });
      }
    })
    .catch((err) => {
      console.warn("Error setting up FCM message listener:", err);
    });

  return () => {
    if (unsubscribe) {
      unsubscribe();
    }
  };
}

const FirebaseService = {
  firebaseApp,
  getAnalyticsInstance,
  getMessagingInstance,
  onForegroundMessage,
  reCreateFcmToken,
};

export default FirebaseService;
